import VueRouter from 'vue-router'
import { mapGetters } from 'vuex'
import Actionsheet from '@/components/Actionsheet'
import { isObject } from '@/utils/helpers'
import { copyToClipboard } from '@/utils/Clipboard'
import config from '@/config'
import store from '@/store'
import { hasCordova, hasDevice, platformIsAndroid, platformIsIOS } from '@/cordova'
import { SecureSaveAccount } from '@/types/CordovaMixin'

export interface WriteFileOptions {
  create?: boolean
  isAppend?: boolean
}

export default {
  computed: {
    ...mapGetters('AppInfo', ['appName', 'appVersion', 'MAIL_ADDRESS_SUPP']),
    ...mapGetters('MyData', ['myName']),

    /**
     * プラットフォームが Android か判定
     */
    $_platformIsAndroid(): boolean {
      return platformIsAndroid
    },

    /**
     * プラットフォームが iSO か判定
     */
    $_platformIsIOS(): boolean {
      return platformIsIOS
    },

    /**
     * デバイスが iPhone X か判定
     */
    $_deviceIsIPhoneX(): boolean {
      return hasCordova && /iPhone10,(3|6)/.test(device.model)
    },

    /**
     * cordova.js が読み込まれているか判定
     */
    $_hasCordova(): boolean {
      return hasCordova
    },

    /**
     * cordova-plugin-device が読み込まれているか判定
     */
    $_hasDevice(): boolean {
      return hasDevice()
    },

    /**
     * お問い合わせ用 mailto のアドレス
     */
    async $_mailTo(this: any): Promise<string> {
      let body = [
        '%0d%0a%0d%0a%0d%0a※以下の情報は削除せずに送信してください。%0d%0a%0d%0a',
        `name: ${this.myName}%0d%0a`
      ].join('')
      if (this.$_hasDevice) {
        const { userId, _ } = await this.$_secureGetAccount()
        body += [
          `userID: ${userId}%0d%0a`,
          `model: ${device.model}%0d%0a`,
          `platform: ${device.platform}%0d%0a`,
          `appVersion: ${this.appVersion}%0d%0a`,
          `osVersion: ${device.version}%0d%0a`
        ].join('')
      }
      return `mailto:${this.MAIL_ADDRESS_SUPP}?subject=お問い合わせ&body=${body}`
    },

    /**
     * SafariViewController の有効判定
     */
    $_isAvaiableSafariViewController(): Promise<boolean> {
      return new Promise(resolve => {
        if (typeof window.SafariViewController === 'undefined') {
          resolve(false)
        } else {
          window.SafariViewController.isAvailable(resolve)
        }
      })
    }
  },
  methods: {
    /**
     * window.open() でブラウザを開く
     * InAppBrowser をインストールしていればデフォルトで InAppBrowser を使用する
     */
    async $_open(url: string, target: string = '_blank') {
      const options = platformIsAndroid
        ? 'hideurlbar=yes,hidenavigationbuttons=no,footer=no'
        : platformIsIOS
        ? 'location=no,zoom=no,toolbarposition=bottom'
        : ''
      let ref = null
      const closeEvent = event => {
        // 無効ページに遷移しそこに URL Scheme が含まれているなら該当ページに遷移
        if (new RegExp(config.URL_SCHEME).test(event.url)) {
          if (ref) {
            ref.close()
            ref.removeEventListener('loadstart', closeEvent)
            ref.removeEventListener('loadstop', closeEvent)
            ref.removeEventListener('loaderror', closeEvent)
          } else if (typeof window.SafariViewController !== 'undefined') {
            window.SafariViewController.hide()
          }
          location.href = '#/' + event.url.split(config.URL_SCHEME)[1]
        }
      }

      if (hasCordova) {
        const avaiableSafariViewController = await this['$_isAvaiableSafariViewController']
        if (avaiableSafariViewController) {
          const opt: SafariViewControllerOptions = {
            url: url,
            transition: 'slide'
          }
          window.SafariViewController.show(opt, result => {
            closeEvent(result)
          })
        } else {
          ref = cordova.InAppBrowser.open(url, target, options)
          if (!ref || typeof ref.removeEventListener === 'undefined') {
            return
          }
          ref.addEventListener('loadstart', closeEvent)
          ref.addEventListener('loadstop', closeEvent)
          ref.addEventListener('loaderror', closeEvent)
        }
      } else {
        window.open(url, target)
      }
    },

    /**
     * alert
     */
    $_alert(message: string, title: string = '', buttonName: string = ''): Promise<void> {
      return new Promise(resolve => {
        if (navigator.notification) {
          navigator.notification.alert(
            message,
            () => {
              resolve()
            },
            title,
            buttonName
          )
        } else {
          window.alert(message)
          resolve()
        }
      })
    },

    /**
     * 確認ダイアログを出す
     * アプリと web で同じような動作を行うようにラップ
     * web だと title と buttonLabels は無視される
     */
    $_confirm(
      message: string,
      title: string = '',
      buttonLabels: string[] = ['キャンセル', 'OK']
    ): Promise<number | boolean> {
      return new Promise(resolve => {
        if (navigator.notification) {
          // Android ではなぜかボタンが逆順になる
          if (platformIsAndroid) {
            buttonLabels.reverse()
          }
          navigator.notification.confirm(
            message,
            buttonIndex => {
              resolve(buttonIndex)
            },
            title,
            buttonLabels
          )
        } else {
          if (window.confirm(message)) {
            resolve(true)
          } else {
            resolve(false)
          }
        }
      })
    },

    /**
     * conrim で OK ボタンをタップしたか判定
     * ボタンは ['Cance', 'OK'] の順番で並んでいるものとする
     * ※Android では向きが逆になっている
     */
    $_clickedConfirmOK(buttonIndex: number | boolean): boolean {
      const isNumber = typeof buttonIndex === 'number'
      if (
        // Android
        (isNumber && platformIsAndroid && buttonIndex === 1) ||
        // iOS
        (isNumber && platformIsIOS && buttonIndex === 2) ||
        // Web
        (typeof buttonIndex === 'boolean' && buttonIndex)
      ) {
        return true
      } else {
        return false
      }
    },

    /**
     * アクションシート表示
     */
    $_showActionsheet(options: ActionsheetOptions): Promise<number> {
      return new Promise((resolve, reject) => {
        if (hasCordova && window.plugins.actionsheet) {
          if (!options.androidTheme) {
            options.androidTheme = window.plugins.actionsheet.ANDROID_THEMES.THEME_HOLO_LIGHT
          }
          if (!options.androidEnableCancelButton) {
            options.androidEnableCancelButton = true
          }
          window.plugins.actionsheet.show(options, resolve, reject)
        } else {
          options.onConfirm = buttonIndex => {
            resolve(buttonIndex)
          }
          Actionsheet(options)
        }
      })
    },

    /**
     * アクションシート非表示
     */
    $_hideActionsheet(options: {}): Promise<number> {
      return new Promise((resolve, reject) => {
        window.plugins.actionsheet.hide(options, resolve, reject)
      })
    },

    /**
     * 画像を取得する
     */
    $_getPicture(this: any, options: CameraOptions): Promise<string> {
      return new Promise(async (resolve, reject) => {
        navigator.camera.getPicture(resolve, reject, options)
      })
    },

    /**
     * 動画を取得する
     */
    $_caputureVideo(): Promise<MediaFile> {
      return new Promise((resolve, reject) => {
        navigator.device.capture.captureVideo(mediaFiles => {
          resolve(mediaFiles[0])
        }, reject)
      })
    },

    /**
     * 一時領域に保存されているファイルのクリーンアップ
     */
    $_cleanupCameraTemp(): Promise<boolean> {
      return new Promise((resolve, reject) => {
        navigator.camera.cleanup(
          () => {
            resolve(true)
          },
          err => {
            console.error(err)
            resolve(false)
          }
        )
      })
    },

    /**
     * capture したビデオの MediaFileData を取得する
     */
    $_getCaptureVideoMediaFileData(mediaFile: MediaFile): Promise<MediaFileData> {
      return new Promise((resolve, reject) => {
        mediaFile.getFormatData(resolve, reject)
      })
    },

    /**
     * 動画を圧縮する
     */
    $_transcodeVideo(options: VideoEditorTranscodeProperties): Promise<string> {
      return new Promise((resolve, reject) => {
        VideoEditor.transcodeVideo(resolve, reject, options)
      })
    },

    /**
     * ファイルのアップロード処理をラップし
     */
    $_uploadFileTransfer(
      fileURL: string,
      server: string,
      options: FileUploadOptions,
      onprogress?: (event: ProgressEvent) => void
    ): Promise<UploadFileResult> {
      return new Promise((resolve, reject) => {
        const ft = new FileTransfer()
        if (typeof onprogress === 'function') {
          ft.onprogress = onprogress
        }
        ft.upload(
          fileURL,
          server,
          (r: FileUploadResult) => {
            const response = isObject(r.response) ? r.response : JSON.parse(r.response)
            const ret: UploadFileResult = {
              data: response,
              fileTransfer: ft
            }
            resolve(ret)
          },
          reject,
          options
        )
      })
    },

    /**
     * ファイルのダウンロードロード処理をラップ
     */
    $_downloadFileTransfer(
      fileUrl: string,
      filePath: string,
      options: FileDownloadOptions,
      onprogress?: (event: ProgressEvent) => void
    ): Promise<any> {
      return new Promise((resolve, reject) => {
        const ft = new FileTransfer()
        if (typeof onprogress === 'function') {
          ft.onprogress = onprogress
        }
        ft.download(fileUrl, filePath, resolve, reject, false, options)
      })
    },

    /**
     * Photo Library の使用許可を必要に応じてリクエスト
     */
    $_mightRequestPhotoLibraryAuthorization(): Promise<boolean> {
      return new Promise(async (resolve, _reject) => {
        let authenticated
        try {
          setTimeout(() => {
            // 5秒待っても authenticated が undefined ならどこかでコケている
            if (typeof authenticated === 'undefined') {
              resolve(true)
            }
          }, 5000)
          authenticated = await this.$_getPhotoLibraryPermission()
          if (!authenticated) {
            await this.$_requestPhotoLibraryAutry()
          }
          resolve(true)
        } catch (err) {
          console.error(err)
          resolve(false)
        }
      })
    },

    /**
     * Photo Library へのアクセス許可をチェック
     */
    $_getPhotoLibraryPermission(): Promise<boolean> {
      return new Promise((resolve, _reject) => {
        cordova.plugins.photoLibrary.getLibrary(
          _chunk => {
            resolve(true)
          },
          (err: string) => {
            resolve(!err.startsWith('Permission'))
          }
        )
      })
    },

    /**
     * PhotoLibrary へのアクセス許可をリクエスト
     */
    $_requestPhotoLibraryAutry(): Promise<any> {
      return new Promise((resolve, reject) => {
        cordova.plugins.photoLibrary.requestAuthorization(resolve, reject, {
          read: true,
          write: true
        })
      })
    },

    /**
     * PhotoLibrary に画像を保存
     */
    $_saveImagePhotoLibrary(url: string, album: string): Promise<PhotoLibraryCordova.LibraryItem> {
      return new Promise((resolve, reject) => {
        cordova.plugins.photoLibrary.saveImage(url, album, resolve, reject)
      })
    },

    /**
     * PhotoLibrary に動画を保存
     */
    $_saveVideoPhotoLibrary(url: string, album: string): Promise<any> {
      return new Promise((resolve, reject) => {
        cordova.plugins.photoLibrary.saveVideo(url, album, resolve, reject)
      })
    },

    // --- cordova-plugin-file ---

    /**
     * 指定パスの entry 一覧を取得する
     */
    $_getFielSystemEntries(path: string): Promise<Entry[]> {
      return new Promise((resolve, reject) => {
        window.resolveLocalFileSystemURL(
          path,
          fileSystem => {
            const reader = fileSystem['createReader']()
            reader.readEntries(resolve, reject)
          },
          reject
        )
      })
    },

    /**
     * ファイルに書き込む
     */
    $_writeFile(
      path: string,
      filename: string,
      dataObj: Blob,
      options: WriteFileOptions = { create: true }
    ): Promise<any> {
      return new Promise(async (resolve, reject) => {
        const fileEntry = options.create
          ? await this.$_createFile(path, filename)
          : await this.$_nativePathToFileEntry(path, filename)
        fileEntry.createWriter((fileWriter: FileWriter) => {
          fileWriter.onwriteend = resolve
          fileWriter.onerror = reject
          if (options.isAppend) {
            try {
              fileWriter.seek(fileWriter.length)
            } catch (err) {
              console.error(`file doesn't exist!`)
            }
          }
          fileWriter.write(dataObj)
        }, reject)
      })
    },

    /**
     * ファイルを読み込む
     */
    $_readFile(path: string, filename: string): Promise<any> {
      return new Promise(async (resolve, reject) => {
        const fileEntry = await this.$_nativePathToFileEntry(path, filename)
        fileEntry.file(file => {
          let reader = new FileReader()
          reader.onloadend = event => {
            resolve(event.target['result'])
          }
          reader.onerror = reject
          reader.readAsText(file)
        }, reject)
      })
    },

    /**
     * ファイルを削除
     */
    $_removeFile(path: string, filename: string): Promise<any> {
      return new Promise(async (resolve, reject) => {
        const fileEntry = await this.$_nativePathToFileEntry(path, filename)
        fileEntry.remove(resolve, reject)
      })
    },

    /**
     * ファイルを作成する
     */
    $_createFile(path: string, fileName: string): Promise<FileEntry> {
      return new Promise(async (resolve, reject) => {
        const dirEntry = await this.$_nativePathToDirEntry(path)
        dirEntry.getFile(fileName, { create: true }, resolve, reject)
      })
    },

    /**
     * ディレクトリを作成する
     */
    $_createDir(path: string, dirname: string): Promise<DirectoryEntry> {
      return new Promise(async (resolve, reject) => {
        const dirEntry = await this.$_nativePathToDirEntry(path)
        dirEntry.getDirectory(dirname, { create: true }, resolve, reject)
      })
    },

    /**
     * ディレクトリを削除
     */
    $_removeDir(path: string): Promise<any> {
      return new Promise(async (resolve, reject) => {
        const dirEntry = await this.$_nativePathToDirEntry(path)
        dirEntry.removeRecursively(resolve, reject)
      })
    },

    /**
     * パスの存在チェック
     */
    $_existsPath(path: string): Promise<boolean> {
      return new Promise(async (resolve, _reject) => {
        window.resolveLocalFileSystemURL(
          path,
          () => {
            resolve(true)
          },
          () => {
            resolve(false)
          }
        )
      })
    },

    /**
     * Native Path をの File Object に変換
     */
    $_nativePathToFile(path: string): Promise<File> {
      return new Promise((resolve, reject) => {
        window.resolveLocalFileSystemURL(
          path,
          entry => {
            const fileEntry = entry as FileEntry
            fileEntry.file(resolve, reject)
          },
          reject
        )
      })
    },

    /**
     * Native Path をの File Type に変換
     */
    async $_nativePathToFileType(path: string): Promise<string> {
      const nativePath = !new RegExp(location.protocol + '//').test(path)
        ? location.protocol + '//' + path
        : path

      const file = await this.$_nativePathToFile(nativePath)
      return file.type
    },

    /**
     * URL を FileEntry に変換する
     */
    $_nativePathToFileEntry(path: string, fileName: string): Promise<FileEntry> {
      return new Promise(async (resolve, reject) => {
        try {
          const dirEntry = await this.$_nativePathToDirEntry(path)
          dirEntry.getFile(fileName, { create: false }, resolve, reject)
        } catch (err) {
          reject(err)
        }
      })
    },

    /**
     * URL を DirectoryEntry に変換する
     */
    $_nativePathToDirEntry(path: string): Promise<DirectoryEntry> {
      return new Promise((resolve, reject) => {
        window.resolveLocalFileSystemURL(
          path,
          entry => {
            resolve(entry as DirectoryEntry)
          },
          reject
        )
      })
    },

    /**
     * アプリデータの保存先を準備
     */
    async $_initAppDataDir(): Promise<string> {
      // ディレクトリの準備
      const path = cordova.file.externalRootDirectory + config.APP_DATA_DIR
      const existsPath = await this.$_existsPath(path)
      if (!existsPath) {
        await this.$_createDir(cordova.file.externalRootDirectory, config.APP_DATA_DIR)
      }
      return path
    },

    /**
     * InAppBrowser でポイントページを開く
     */
    $_openPointPage(this: any, router: VueRouter) {
      const ref = this.$_open(config.PAYMENT_URL + this.$_appBrowserQuery)
      if (!ref || typeof ref.removeEventListener === 'undefined') {
        return
      }

      const closeEvent = event => {
        if (event.url.match('inappbrowser/close')) {
          ref.close()
          ref.removeEventListener('loadstop', closeEvent)
        } else if (event.url.match('inappbrowser/storepayment')) {
          ref.close()
          router.push('/buy/store')
          ref.removeEventListener('loadstop', closeEvent)
        }
      }

      ref.addEventListener('loadstop', closeEvent)
    },

    /**
     * KeyChain からデータを取り出す
     */
    $_getKeyChain(key: string): Promise<string> {
      return new Promise(resolve => {
        window.Keychain.get(
          resolve,
          err => {
            resolve('')
            console.error(err)
          },
          key
        )
      })
    },

    /**
     * KeyChain にデータをセットする
     */
    $_setKeyChain(key: string, value: any): Promise<string> {
      return new Promise((resolve, reject) => {
        window.Keychain.set(resolve, reject, key, value)
      })
    },

    /**
     * KeyChain のデータを削除
     */
    $_removeKeyChain(key: string): Promise<any> {
      return new Promise((resolve, reject) => {
        window.Keychain.remove(resolve, reject, key)
      })
    },

    /**
     * AES 256 CBC で暗号化
     */
    $_encrypt(data: string): Promise<any> {
      return new Promise((resolve, reject) => {
        cordova.plugins.AES256.encrypt(config.SECURE_KEY, config.SECURE_IV, data, resolve, reject)
      })
    },

    /**
     * AES 256 CBC で復号化
     */
    $_decrypt(encryptedData: string): Promise<any> {
      return new Promise((resolve, reject) => {
        cordova.plugins.AES256.decrypt(
          config.SECURE_KEY,
          config.SECURE_IV,
          encryptedData,
          resolve,
          reject
        )
      })
    },

    /**
     * クリップボードにコピー
     */
    $_copyToClipboard(this: any, text: string): Promise<any> {
      return new Promise(resolve => {
        if (this.$_hasCordova) {
          cordova.plugins.clipboard.copy(
            text,
            () => {
              resolve(true)
            },
            () => {
              resolve(false)
            }
          )
        } else {
          resolve(copyToClipboard(text))
        }
      })
    },

    /**
     * Native 設定を開く
     */
    $_openNativeSettings(setting: string): Promise<any> {
      return new Promise((resolve, reject) => {
        cordova.plugins.settings.open(setting, resolve, reject)
      })
    },

    /**
     * メディアやファイルへのアクセスが許可されているかチェックする
     */
    $_checkAllowAccessToMediaFiles(): Promise<boolean> {
      return new Promise(async (resolve, _reject) => {
        const checkPermissions = [
          cordova.plugins.permissions.READ_EXTERNAL_STORAGE,
          cordova.plugins.permissions.WRITE_EXTERNAL_STORAGE
        ]
        try {
          const { hasPermission } = await this.$_requestAndroidPermissions(checkPermissions)
          return resolve(hasPermission)
        } catch (err) {
          console.error(err)
          return resolve(false)
        }
      })
    },

    /**
     * Anroid の Runtime Permission をチェックする
     */
    $_checkAndroidPermission(permission: string): Promise<boolean> {
      return new Promise((resolve, reject) => {
        const permissions = cordova.plugins.permissions
        permissions.checkPermission(
          permission,
          status => {
            resolve(status.hasPermission)
          },
          reject
        )
      })
    },

    /**
     * Anroid の Runtime Permission 許可をまとめてリクエストする
     */
    $_requestAndroidPermissions(permissions: string[]): Promise<any> {
      return new Promise((resolve, reject) => {
        cordova.plugins.permissions.requestPermissions(permissions, resolve, reject)
      })
    },

    /**
     * Android のバックボタン無効化
     */
    $_disableBackbutton() {
      document.addEventListener('backbutton', this.$__backbuttonHandler, false)
    },

    /**
     * Android のバックボタン有効化
     */
    $_enableBackbutton() {
      document.removeEventListener('backbutton', this.$__backbuttonHandler, false)
    },

    $__backbuttonHandler(e) {
      e.preventDefault()
    },

    /**
     * Android の ringer mode を取得する
     */
    $_getAndroindRingerMode(): Promise<string> {
      return new Promise((resolve, reject) => {
        window.RingerMode.getMode(resolve, reject)
      })
    },

    /**
     * ローカル通知を表示
     */
    $_showLocalNotification(params: any) {
      const sound = store.getters['NotificationSetting/app_notice_sound'] === 1
      const vibrate = store.getters['NotificationSetting/app_notice_vib'] === 1
      const defaultParams = {
        priority: 2,
        channel: config.NOTIFICATION_CHANNEL_ID,
        smallIcon: 'res://ic_launcher.png',
        color: config.MAIN_COLOR,
        sound: sound,
        vibrate: vibrate,
        foreground: true
      }
      // sound を使わない場合は channel を消すことで強制的に音を音を鳴らなくする
      if (!sound) {
        delete params.channel
      }
      if (!vibrate) {
        params.vibrate = false
      }
      cordova.plugins.notification.local.schedule({
        ...defaultParams,
        ...params
      })
    },

    // --- Adjust ---

    /**
     * googleAdId を取得
     */
    $_getGoogleAdId(): Promise<string> {
      return new Promise((resolve, _reject) => {
        window.Adjust.getGoogleAdId((googleAdId: string) => {
          resolve(googleAdId)
        })
      })
    },

    /**
     * adid を取得
     */
    $_getAdid(): Promise<string> {
      return new Promise((resolve, _reject) => {
        window.Adjust.getAdid((adid: string) => {
          resolve(adid)
        })
      })
    },

    // ----- account -----

    /**
     * セキュアにユーザー情報を保存する
     * historyId はユニークな登録かを判定するために端末に保存される情報
     */
    async $_secureSaveAccount({ userId, password, historyId }: SecureSaveAccount): Promise<any> {
      try {
        const encryptedUserId = await this.$_encrypt(userId)
        const encryptedPassword = await this.$_encrypt(password)
        const encryptedHistoryId = await this.$_encrypt(historyId)

        if (platformIsAndroid) {
          const appDataPath = (await this.$_initAppDataDir()) + '/'
          // Android は外部ストレージに保存できなかったときに備えてネイティブストレージにも保存
          await this.$_writeFile(appDataPath, 'ai', encryptedUserId)
          await this.$_writeFile(appDataPath, 'ap', encryptedPassword)
          await this.$_setItemNativeStorage('ai', encryptedUserId)
          await this.$_setItemNativeStorage('ap', encryptedPassword)
          if (historyId) {
            await this.$_writeFile(appDataPath, 'data.txt', encryptedHistoryId)
            await this.$_setItemNativeStorage('history_id', encryptedHistoryId)
          }
        } else if (platformIsIOS) {
          await this.$_setKeyChain('user_id', encryptedUserId)
          await this.$_setKeyChain('password', encryptedPassword)
          if (historyId) {
            await this.$_setKeyChain('history_id', encryptedHistoryId)
          }
        }
      } catch (err) {
        console.error(err)
      }
    },

    /**
     * セキュアに保存したユーザー情報を取得する
     */
    async $_secureGetAccount(): Promise<SecureSaveAccount> {
      try {
        let [encryptedUserId, encryptedPassword, encryptedHistoryId] = ['', '', '']

        if (platformIsAndroid) {
          try {
            const appDataPath = cordova.file.externalRootDirectory + config.APP_DATA_DIR + '/'
            if (
              (await this.$_existsPath(appDataPath + 'ai')) &&
              (await this.$_existsPath(appDataPath + 'ap')) &&
              (await this.$_existsPath(appDataPath + 'data.txt'))
            ) {
              encryptedUserId = await this.$_readFile(appDataPath, 'ai')
              encryptedPassword = await this.$_readFile(appDataPath, 'ap')
              encryptedHistoryId = await this.$_readFile(appDataPath, 'data.txt')
            }
          } catch (err) {
            encryptedUserId = await this.$_getItemNativeStorage('ai')
            encryptedPassword = await this.$_getItemNativeStorage('ap')
            encryptedHistoryId = await this.$_getItemNativeStorage('history_id')
          }
        } else if (platformIsIOS) {
          encryptedUserId = await this.$_getKeyChain('user_id')
          encryptedPassword = await this.$_getKeyChain('password')
          encryptedHistoryId = await this.$_getKeyChain('history_id')
        }

        return {
          userId: await this.$_decrypt(encryptedUserId),
          password: await this.$_decrypt(encryptedPassword),
          historyId: await this.$_decrypt(encryptedHistoryId)
        }
      } catch (err) {
        console.error(err)
      }
      return null
    },

    /**
     * 端末のキャッシュをクリア
     */
    $_clearCache(): Promise<any> {
      return new Promise((resolve, reject) => {
        window.CacheClear(resolve, reject)
      })
    },

    // --- Native Storage ---

    /**
     * NativeStorage にアイテムをセット
     */
    $_setItemNativeStorage(key: string, value: any): Promise<any> {
      return new Promise((resolve, reject) => {
        window.NativeStorage.setItem(key, value, resolve, reject)
      })
    },

    /**
     * NativeStorage からアイテムを取得
     */
    $_getItemNativeStorage(key: string): Promise<any> {
      return new Promise((resolve, reject) => {
        window.NativeStorage.getItem(key, resolve, reject)
      })
    },

    /**
     * NativeStorage から key 一覧を取得
     */
    $_keysNativeStorage(): Promise<string[]> {
      return new Promise((resolve, reject) => {
        window.NativeStorage.keys(resolve, reject)
      })
    },

    /**
     * NativeStorage からアイテムを削除
     */
    $_removeNativeStorage(key: string): Promise<any> {
      return new Promise((resolve, reject) => {
        window.NativeStorage.remove(key, resolve, reject)
      })
    },

    /**
     * NativeStorage のアイテムをクリア
     */
    $_clearNativeStorage(): Promise<any> {
      return new Promise((resolve, reject) => {
        window.NativeStorage.clear(resolve, reject)
      })
    },

    /**
     * iOS only
     * NativeStorage アプリ間でデータを共有するためにスイート名を指定して初期化
     */
    $_initWithSuiteNameNativeStorage(suitename: string): Promise<string[]> {
      return new Promise((resolve, reject) => {
        window.NativeStorage.initWithSuiteName(suitename)
      })
    }
  }
}
